import { ArrowBackOutlined as ArrowBackOutlinedIcon, AccessTimeOutlined as AccessTimeOutlinedIcon, PersonOutlineOutlined as PersonOutlineOutlinedIcon } from "../../../components/icons";
import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import dayjs from "dayjs";
import Loader from "../../../components/Loader";
import {
  formatOperationsStatus,
  getOperationsStatusSx,
  ifasColors,
  OperationsEmptyState,
  OperationsPageShell,
  OperationsSectionHeader,
  operationsDetailPaperSx,
} from "../ifas/components/OperationsUI";

type MortuaryHistoryTimelineProps = {
  historyData: any[];
  isLoading: boolean;
  recordSummary?: any;
  onBack: () => void;
};

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format("DD MMM YYYY HH:mm") : "Not captured";

const getEventTime = (item: any) =>
  item?.actionAt ||
  item?.updated_on ||
  item?.updatedOn ||
  item?.created_on ||
  item?.createdOn ||
  null;

const stageRank = (stage?: string | null) => {
  const normalized = String(stage || "").toUpperCase();
  const ranks: Record<string, number> = {
    ARRIVAL: 1,
    WASHING: 2,
    CLOTHES: 3,
    DRESSING: 4,
    COFFINING: 5,
    DELIVERY: 6,
  };
  return ranks[normalized] || 99;
};

const eventRank = (item: any) => {
  const eventType = String(item?.eventType || "").toUpperCase();
  switch (eventType) {
    case "RECORD_CREATED":
      return 0;
    case "INTAKE_REGISTERED":
      return 10;
    case "MEASUREMENT_UPDATED":
      return 20;
    case "STAGE_STARTED":
      return 100 + stageRank(item?.bodyControlStage) * 10 + 1;
    case "STAGE_COMPLETED":
      return 100 + stageRank(item?.bodyControlStage) * 10 + 2;
    case "BODY_DISPATCH_CREATED":
      return 300;
    case "INTERNAL_TRANSFER_SUBMITTED":
      return 310;
    case "INTERNAL_TRANSFER_RECEIVED":
      return 320;
    case "EXTERNAL_RELEASE_COMPLETED":
      return 330;
    case "FAMILY_DELIVERY_COMPLETED":
      return 340;
    case "EASIPOL_UPLOADED":
      return 350;
    default:
      return 999;
  }
};

const buildContextNote = (item: any) => {
  const metadata = item?.metadata || {};

  if (metadata.dispatchNumber) {
    return `Dispatch ${metadata.dispatchNumber}`;
  }
  if (metadata.registerNumber) {
    return `Register ${metadata.registerNumber}`;
  }
  if (metadata.bodyNumber) {
    return `Body ${metadata.bodyNumber}`;
  }
  if (metadata.shelfNumber) {
    return `Shelf ${metadata.shelfNumber}`;
  }
  if (metadata.receivingMortuary) {
    return `Receiving mortuary ${metadata.receivingMortuary}`;
  }
  if (metadata.parlourName) {
    return `Parlour ${metadata.parlourName}`;
  }
  if (metadata.recipientName) {
    return `Recipient ${metadata.recipientName}`;
  }

  return null;
};

const summaryChips = (recordSummary?: any) =>
  [
    recordSummary?.bodyRemovalNumber
      ? `Body Removal ${recordSummary.bodyRemovalNumber}`
      : null,
    recordSummary?.caseNumber ? `Case ${recordSummary.caseNumber}` : null,
    recordSummary?.mortuaryName ? recordSummary.mortuaryName : null,
    recordSummary?.bodyNumber ? `Body ${recordSummary.bodyNumber}` : null,
  ].filter(Boolean) as string[];

export default function MortuaryHistoryTimeline({
  historyData,
  isLoading,
  recordSummary,
  onBack,
}: MortuaryHistoryTimelineProps) {
  const visibleHistory = (() => {
    const withoutSystemCreate = historyData.filter(
      (item) => item?.eventType !== "RECORD_CREATED",
    );
    const source = withoutSystemCreate.length > 0 ? withoutSystemCreate : historyData;
    return [...source].sort((left, right) => {
      const leftTime = getEventTime(left) ? dayjs(getEventTime(left)).valueOf() : 0;
      const rightTime = getEventTime(right) ? dayjs(getEventTime(right)).valueOf() : 0;
      if (leftTime !== rightTime) {
        return leftTime - rightTime;
      }
      return eventRank(left) - eventRank(right);
    });
  })();

  return (
    <OperationsPageShell
      eyebrow="Mortuary History"
      title="Mortuary Activity"
      description="A simple timeline of who did what, when it happened, and how the mortuary stage moved forward."
      actions={
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ArrowBackOutlinedIcon />}
            onClick={onBack}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 800 }}
          >
            Back To Record
          </Button>
        </Box>
      }
      meta={
        <Box display="flex" gap={0.75} flexWrap="wrap">
          {summaryChips(recordSummary).map((label) => (
            <Chip
              key={label}
              size="small"
              label={label}
              sx={{
                bgcolor: "#fff",
                color: ifasColors.text,
                border: `1px solid ${ifasColors.border}`,
                fontWeight: 700,
              }}
            />
          ))}
        </Box>
      }
    >
      <Paper elevation={0} sx={operationsDetailPaperSx}>
        {isLoading ? <Loader isLoading={isLoading} /> : null}

        <OperationsSectionHeader
          title="Timeline"
          meta={`${visibleHistory?.length || 0} event${visibleHistory?.length === 1 ? "" : "s"}`}
        />

        {isLoading ? (
          <Box minHeight={160} />
        ) : visibleHistory.length === 0 ? (
          <OperationsEmptyState
            title="No history captured yet"
            description="The mortuary timeline will appear here once intake, body control, or dispatch-related actions are recorded."
          />
        ) : (
          <Box sx={{ position: "relative", mt: 1.5, pl: { xs: 1.25, sm: 2 } }}>
            <Box
              sx={{
                position: "absolute",
                top: 4,
                bottom: 4,
                left: { xs: 8, sm: 12 },
                width: 2,
                bgcolor: "#F1F2F4",
                borderRadius: 999,
              }}
            />

            <Box sx={{ display: "grid", gap: 1.5 }}>
              {visibleHistory.map((item, index) => {
                const contextNote = buildContextNote(item);

                return (
                  <Box
                    key={`${item.eventType}-${item.actionAt || item.created_on || index}`}
                    sx={{ position: "relative", pl: { xs: 2.5, sm: 3 } }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 20,
                        left: { xs: 0, sm: 0.5 },
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: ifasColors.orange,
                        boxShadow: "0 0 0 4px rgba(236,110,0,0.12)",
                      }}
                    />

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${ifasColors.border}`,
                        bgcolor: "#fff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 220 }}>
                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 900,
                              color: ifasColors.text,
                              lineHeight: 1.2,
                            }}
                          >
                            {formatOperationsStatus(item.eventType)}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.5,
                              fontSize: 13.5,
                              color: ifasColors.muted,
                              lineHeight: 1.6,
                            }}
                          >
                            {item.eventDescription || "Mortuary activity recorded"}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: ifasColors.muted,
                            flexShrink: 0,
                          }}
                        >
                          <AccessTimeOutlinedIcon sx={{ fontSize: 15 }} />
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                            Time: {formatDateTime(getEventTime(item))}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          mt: 1.25,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          flexWrap: "wrap",
                        }}
                      >
                        {item.mortuaryStatus ? (
                          <Chip
                            size="small"
                            label={formatOperationsStatus(item.mortuaryStatus)}
                            sx={{ fontWeight: 800, ...getOperationsStatusSx(item.mortuaryStatus) }}
                          />
                        ) : null}
                        {item.bodyControlStage ? (
                          <Chip
                            size="small"
                            label={formatOperationsStatus(item.bodyControlStage)}
                            sx={{
                              bgcolor: "#FFF4EB",
                              color: ifasColors.orange,
                              fontWeight: 800,
                            }}
                          />
                        ) : null}
                        {item.stageStatus ? (
                          <Chip
                            size="small"
                            label={formatOperationsStatus(item.stageStatus)}
                            sx={{
                              bgcolor: "#F4F3FF",
                              color: "#4338CA",
                              fontWeight: 800,
                            }}
                          />
                        ) : null}
                      </Box>

                      <Box
                        sx={{
                          mt: 1.25,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: ifasColors.text,
                          }}
                        >
                          <PersonOutlineOutlinedIcon sx={{ fontSize: 15 }} />
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            Performed By: {item.performedBy || "System"}
                          </Typography>
                        </Box>

                        {item.roleAtTime ? (
                          <Typography sx={{ fontSize: 12.5, color: ifasColors.muted }}>
                            {formatOperationsStatus(item.roleAtTime)}
                          </Typography>
                        ) : null}

                        {contextNote ? (
                          <Typography sx={{ fontSize: 12.5, color: ifasColors.muted }}>
                            {contextNote}
                          </Typography>
                        ) : null}
                      </Box>
                    </Paper>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Paper>
    </OperationsPageShell>
  );
}
